import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import axios from 'axios';
import { Smartphone, Search } from 'react-feather';
import { Spinner } from '../../../../AbstractElements';
import { toast } from 'react-toastify';
import { Label, Input, Media } from 'reactstrap';
import config from '../../../../config';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../admin-v3.css';
import v3DataTableStyles from '../v3DataTable';
import v3SelectStyles from '../v3Select';

const PortalManage = () => {
    const { t } = useTranslation();
    const canManage = localStorage.getItem('tenantPortalManage') === '1';

    const [tenants, setTenants] = useState([]);
    const [allTenants, setAllTenants] = useState([]);
    const [properties, setProperties] = useState([]);
    const [property_id, setPropertyId] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const property_access = localStorage.getItem('propertyAccess');
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };

    // Authorize in an effect so the redirect doesn't fire during render.
    useEffect(() => {
        if (!canManage) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canManage]);

    useEffect(() => {
        axios
            .post(`${config.hostname}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaire_id, property_id: property_access }, configheader)
            .then((response) => setProperties(response.data))
            .catch((error) => console.log(error));
    }, []);

    useEffect(() => {
        axios
            .post(`${config.hostname}/tenant/getenantsbyconcessionaire`, { tenant_concessionaire_id: concessionaire_id, property_id: property_access }, configheader)
            .then((response) => {
                setAllTenants(response.data);
                setTenants(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    }, []);

    // Single source of filtering: property + search applied together.
    useEffect(() => {
        let filtered = allTenants;
        if (property_id !== '') {
            filtered = filtered.filter((t) => t.tenant_property_id?._id === property_id);
        }
        const q = searchTerm.trim().toLowerCase();
        if (q !== '') {
            filtered = filtered.filter(
                (t) =>
                    (t.tenant_store_name && t.tenant_store_name.toLowerCase().includes(q)) ||
                    (t.tenant_code && t.tenant_code.toLowerCase().includes(q)),
            );
        }
        setTenants(filtered);
    }, [property_id, searchTerm, allTenants]);

    const handleManualChange = (id, isChecked, tenantName) => {
        const prev = tenants;
        // Optimistic; revert on failure.
        setTenants((list) => list.map((t) => (t._id === id ? { ...t, tenant_manual_flag: isChecked } : t)));
        setAllTenants((list) => list.map((t) => (t._id === id ? { ...t, tenant_manual_flag: isChecked } : t)));
        axios
            .post(`${config.hostname}/tenant/updatetenant/${id}`, { login_id, tenant_manual_flag: isChecked }, configheader)
            .then(() => {
                insertToLog(`${isChecked ? ' enabled' : ' disabled'} manual portal access to ${tenantName}`);
            })
            .catch((error) => {
                setTenants(prev);
                setAllTenants((list) => list.map((t) => (t._id === id ? { ...t, tenant_manual_flag: !isChecked } : t)));
                toast.error(t('Could not update — reverted'));
                console.log(error);
            });
    };

    const propertyOptions = useMemo(
        () => [{ value: '', label: t('All properties') }, ...properties.map((p) => ({ value: p._id, label: p.property_name }))],
        [properties, t],
    );

    const insertToLog = (task) => {
        axios
            .post(`${config.hostname}/log`, { created_by: login_id, concessionaire_id, module: 'Admin Settings', task: login_name + task }, configheader)
            .catch((error) => console.log(error));
    };

    const tenantColumns = [
        { name: t('Store Name'), selector: (row) => row.tenant_store_name, sortable: true, wrap: true, minWidth: '160px' },
        { name: t('Tenant Code'), selector: (row) => row.tenant_code, sortable: true, wrap: true, minWidth: '120px' },
        { name: t('Property'), selector: (row) => row.tenant_property_id?.property_name, sortable: true, wrap: true, minWidth: '140px' },
        {
            name: t('Portal Access'),
            cell: (row) => (
                <Media key={row._id}>
                    <Media body className='text-end icon-state switch-outline'>
                        <Label className='switch'>
                            <Input
                                type='checkbox'
                                checked={!!row.tenant_manual_flag}
                                disabled={!canManage}
                                onChange={(e) => handleManualChange(row._id, e.target.checked, row.tenant_store_name)}
                            />
                            <span className='switch-state'></span>
                        </Label>
                    </Media>
                </Media>
            ),
            sortable: false,
            minWidth: '120px',
        },
    ];

    if (!canManage) return null;

    return (
        <Fragment>
            <div className="v3-sadmin v3-admin-page">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('Admin settings')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{t('Tenant Portal')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {t('Enable or disable tenant-portal access per store.')}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        <Select
                            classNamePrefix="v3sel"
                            styles={v3SelectStyles}
                            options={propertyOptions}
                            value={propertyOptions.find((o) => o.value === property_id) || propertyOptions[0]}
                            onChange={(opt) => setPropertyId(opt ? opt.value : '')}
                            isSearchable
                            placeholder={t('Search property…')}
                            aria-label={t('Property filter')}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                        <label className="v3-sadmin-search">
                            <Search size={14} />
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t('Search store / code…')}
                                aria-label={t('Search tenants')}
                            />
                        </label>
                        <span className="v3-sadmin-kpi-icon accent" aria-hidden><Smartphone size={18} /></span>
                    </div>
                </div>

                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-sadmin-list-header">
                        <div className="v3-sadmin-list-title">
                            <h2 className="v3-title">{t('Tenants')}</h2>
                            <span className="v3-sadmin-list-count">{tenants.length}</span>
                        </div>
                    </div>
                    <div style={{ padding: '4px 8px 8px' }}>
                        {loading ? (
                            <div className="loader-box"><Spinner attrSpinner={{ className: 'loader-3' }} /></div>
                        ) : (
                            <DataTable
                                columns={tenantColumns}
                                data={tenants}
                                pagination
                                highlightOnHover
                                responsive
                                customStyles={v3DataTableStyles}
                                noDataComponent={t('No tenants found.')}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default PortalManage;
